import { Dispatch } from "redux";
import { exportNode } from "../../api/title";
import { TitleTree, NodeTypeName, ExportOptions, PrintoutVersion, AllEditorialTypes, ExportType } from "../../domain/domain";
import { UiActionType } from "../../reducers/ui.reducer";
import {
    XML_AND_PROVISIONS_PRINTOUT_NODES,
    CERTIFIED_COPY_NODES,
    DISABLED_UPDATES_PRINTOUT_CITATIONS,
    EXPORT_FILE_EXTENSIONS,
    EXPORT_FILENAME_PREFIXES,
    SHOW_HEADING_NODES,
    COMPONENT_SELECTION_PANEL_EXPORT_TYPES
} from "./exportConstants";

export function getIdentifier(tree: TitleTree): string {
    return tree.citation || tree.heading;
}

export function isEnableExportToXMLOrProvisionsPrintout(subTree: TitleTree): boolean {
    return XML_AND_PROVISIONS_PRINTOUT_NODES.includes(subTree.nodeType);
}

export function isEnableCertifiedCopy(subTree: TitleTree): boolean {
    return CERTIFIED_COPY_NODES.includes(subTree.nodeType);
}

export function isEnableUpdatesPrintout(subTree: TitleTree): boolean {
    return subTree.nodeType === NodeTypeName.TITLE && !DISABLED_UPDATES_PRINTOUT_CITATIONS.includes(subTree.citation);
}

export function isEnableTitleAnalysis(subTree: TitleTree): boolean {
    return subTree.nodeType === NodeTypeName.TITLE;
}

export function isEnableArticleAnalysis(subTree: TitleTree): boolean {
    return subTree && !subTree.repealed && subTree.nodeType === NodeTypeName.ARTICLE;
}

export function isEnableHeadingOnly(subTree: TitleTree): boolean {
    return subTree && !subTree.repealed && SHOW_HEADING_NODES.includes(subTree.nodeType);
}

export function isPrintDisabled(exportOptions: ExportOptions, loading: boolean): boolean {
    if (loading) {
        return true;
    }

    if (exportOptions.editorialContentPrintoutVersion === PrintoutVersion.NONE) {
        return false;
    }

    return !AllEditorialTypes.some(type => exportOptions.editorialTypes.includes(type));
}

export function shouldUseComponentSelectionPanel(exportType: ExportType): boolean {
    return COMPONENT_SELECTION_PANEL_EXPORT_TYPES.includes(exportType);
}

// Makes an API call to export a node and returns the response as a Blob
export async function performExport(nodeId: number, exportOptions: ExportOptions): Promise<Blob> {
    const response = await exportNode(nodeId, exportOptions);
    return new Blob([response.data]);
}

// Generates a filename for the exported file based on node data and export type
export function generateFileName(subTree: TitleTree, exportType: ExportType): string {
    const exportTypeStr = ExportType[exportType];
    let fileExtension = EXPORT_FILE_EXTENSIONS[exportTypeStr] || EXPORT_FILE_EXTENSIONS.default;
    const identifier = getIdentifier(subTree);
    const prefix = EXPORT_FILENAME_PREFIXES[exportTypeStr] || EXPORT_FILENAME_PREFIXES.default;

    // Special case: Title 24 exports should use .zip extension
    if (exportType === ExportType.EXPORT_TO_XML && identifier === "24") {
        fileExtension = "zip";
    }

    return `${prefix}_${identifier}.${fileExtension}`;
}

// Creates and triggers a download for a Blob with the given filename
export function downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

// Coordinates the export process and handles UI state management
export async function handleExport(
    subTree: TitleTree,
    selectedExportOptions: ExportOptions,
    setLoading: (loading: boolean) => void,
    dispatch: Dispatch<any>
): Promise<void> {
    setLoading(true);
    try {
        const blob = await performExport(subTree.nodeId, selectedExportOptions);
        const fileName = generateFileName(subTree, selectedExportOptions.exportType);
        downloadBlob(blob, fileName);
        dispatch({
            type: UiActionType.PRINTING_OPTIONS_DIALOG_OPEN,
            open: false
        });
    } catch (error) {
        console.error(error);
        dispatch({
            type: UiActionType.SNACKBAR_STATE_CHANGED,
            newState: {
                open: true,
                message: "Could not print component",
                severity: "error"
            }
        });
    } finally {
        setLoading(false);
    }
}